import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, UserCheck, UserX, X, Loader2 } from 'lucide-react';
import AdminLayout from './AdminLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PendingApprovalsTab() {
    const { getAuthHeader } = useAuth();
    const [pendingUsers, setPendingUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [approving, setApproving] = useState(null);
    const [showApproveDialog, setShowApproveDialog] = useState(null);
    const [approveRole, setApproveRole] = useState('sales_rep');

    useEffect(() => {
        loadPending();
    }, []);

    const loadPending = async () => {
        try {
            const res = await axios.get(`${API}/admin/pending-users`, { headers: getAuthHeader() });
            setPendingUsers(res.data || []);
        } catch { /* ignore */ }
        finally { setLoading(false); }
    };

    const handleApprove = async (userId) => {
        setApproving(userId);
        try {
            await axios.put(`${API}/admin/users/${userId}/approve`, { role: approveRole }, { headers: getAuthHeader() });
            toast.success('User approved');
            setShowApproveDialog(null);
            loadPending();
        } catch { toast.error('Failed to approve user'); }
        finally { setApproving(null); }
    };

    const handleReject = async (userId) => {
        if (!window.confirm('Are you sure you want to reject this user?')) return;
        setApproving(userId);
        try {
            await axios.put(`${API}/admin/users/${userId}/reject`, {}, { headers: getAuthHeader() });
            toast.success('User rejected');
            loadPending();
        } catch { toast.error('Failed to reject user'); }
        finally { setApproving(null); }
    };

    if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-stone-400 animate-spin" /></div>;

    if (pendingUsers.length === 0) {
        return (
            <div className="text-center py-16">
                <UserCheck className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                <p className="text-sm text-stone-500">No pending approvals</p>
                <p className="text-xs text-stone-400 mt-1">Users who register with your organization's email domain will appear here for approval.</p>
            </div>
        );
    }

    return (
        <div>
            <div className="flex items-center gap-2 mb-4">
                <Clock className="w-5 h-5 text-amber-600" />
                <h3 className="text-sm font-semibold text-stone-700">{pendingUsers.length} user{pendingUsers.length !== 1 ? 's' : ''} waiting for approval</h3>
            </div>
            <div className="space-y-3">
                {pendingUsers.map(u => (
                    <div key={u.user_id} className="flex items-center justify-between p-4 bg-amber-50/50 border border-amber-200/60 rounded-xl">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-sm font-bold text-amber-700">
                                {(u.name || u.email || '?').charAt(0).toUpperCase()}
                            </div>
                            <div>
                                <p className="text-sm font-medium text-stone-900">{u.name || 'Unknown'}</p>
                                <p className="text-xs text-stone-500">{u.email}</p>
                                <p className="text-xs text-stone-400 mt-0.5">
                                    Registered {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'recently'}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {showApproveDialog === u.user_id ? (
                                <div className="flex items-center gap-2">
                                    <select value={approveRole} onChange={e => setApproveRole(e.target.value)}
                                        className="px-2 py-1.5 text-xs border border-stone-200 rounded-lg bg-white">
                                        <option value="sales_rep">Sales Rep</option>
                                        <option value="manager">Manager</option>
                                        <option value="admin">Admin</option>
                                    </select>
                                    <button onClick={() => handleApprove(u.user_id)} disabled={approving === u.user_id}
                                        className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50">
                                        {approving === u.user_id ? '...' : 'Confirm'}
                                    </button>
                                    <button onClick={() => setShowApproveDialog(null)}
                                        className="px-2 py-1.5 text-xs text-stone-500 hover:text-stone-700">
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <button onClick={() => { setApproveRole('sales_rep'); setShowApproveDialog(u.user_id); }}
                                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
                                        <UserCheck className="w-3.5 h-3.5" /> Approve
                                    </button>
                                    <button onClick={() => handleReject(u.user_id)} disabled={approving === u.user_id}
                                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50">
                                        <UserX className="w-3.5 h-3.5" /> Reject
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function AdminPendingApprovalsPage() {
    return (
        <AdminLayout title="Pending Approvals" description="Review and approve user registration requests">
            <PendingApprovalsTab />
        </AdminLayout>
    );
}
